import React from "react";
import { Cloud, CircleCheckBig, CloudOff, CloudAlert, RefreshCw, TriangleAlert } from "lucide-react";

/**
 * Small status badge shown at the top of the Log panel for Premium users.
 * Reports the live sync state produced by useCloudSync().
 *
 * Design:
 * - Rune-bordered pill; icon + label + relative time
 * - Click = manual sync (unless state === "syncing")
 * - Colour tokens match the fantasy palette (gold/arcane/ruby)
 */

function formatRelative(iso) {
  if (!iso) return "mai";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 5) return "adesso";
  if (s < 60) return `${s}s fa`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  const d = Math.floor(h / 24);
  return `${d}g fa`;
}

const CONFIG = {
  idle: {
    Icon: Cloud,
    label: "In attesa",
    cls: "border-border text-muted-foreground bg-card/50",
    spin: false,
  },
  syncing: {
    Icon: RefreshCw,
    label: "Sincronizzando…",
    cls: "border-arcane/60 text-arcane bg-arcane/10",
    spin: true,
  },
  synced: {
    Icon: CircleCheckBig,
    label: "Sincronizzato",
    cls: "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
    spin: false,
  },
  offline: {
    Icon: CloudOff,
    label: "Offline",
    cls: "border-border text-muted-foreground bg-card/60",
    spin: false,
  },
  conflict: {
    Icon: TriangleAlert,
    label: "Modifiche remote",
    cls: "border-amber-500/60 text-amber-400 bg-amber-500/10",
    spin: false,
  },
  error: {
    Icon: CloudAlert,
    label: "Errore sync",
    cls: "border-ruby/60 text-ruby bg-ruby/10",
    spin: false,
  },
};

export default function CloudSyncBadge({
  state = "idle",
  lastSyncedAt,
  driftInfo,
  errorMsg,
  onSyncNow,
  onAcknowledgeConflict,
}) {
  const cfg = CONFIG[state] || CONFIG.idle;
  const { Icon } = cfg;
  const disabled = state === "syncing";

  const tooltipParts = [];
  if (state === "error" && errorMsg) tooltipParts.push(errorMsg);
  if (state === "conflict" && driftInfo?.onlyLocal?.length) {
    tooltipParts.push(`${driftInfo.onlyLocal.length} log rimosso da altrove`);
  }
  const tooltip = tooltipParts.join(" · ");

  return (
    <div
      className="rune-border rounded-md p-2 mb-2 flex items-center gap-2 text-[10px]"
      data-testid="cloud-sync-badge"
      data-sync-state={state}
    >
      <button
        onClick={onSyncNow}
        disabled={disabled}
        title={tooltip || "Sincronizza ora"}
        className={`flex items-center gap-1.5 px-2 py-1 rounded border font-heading tracking-widest uppercase transition-all hover:brightness-125 active:scale-95 disabled:cursor-not-allowed ${cfg.cls}`}
        data-testid="cloud-sync-badge-btn"
      >
        <Icon className={`w-3.5 h-3.5 ${cfg.spin ? "animate-spin" : ""}`} />
        <span data-testid="cloud-sync-badge-label">{cfg.label}</span>
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-muted-foreground truncate" data-testid="cloud-sync-badge-time">
          Ultimo sync: <span className="text-parchment">{formatRelative(lastSyncedAt)}</span>
        </p>
        {state === "conflict" && driftInfo?.onlyLocal?.length > 0 && (
          <button
            onClick={onAcknowledgeConflict}
            className="text-[9px] text-amber-400 hover:text-amber-300 underline"
            data-testid="cloud-sync-conflict-ack"
          >
            {driftInfo.onlyLocal.length} log rimossi altrove — Conferma
          </button>
        )}
        {state === "error" && errorMsg && (
          <p className="text-[9px] text-ruby truncate" title={errorMsg} data-testid="cloud-sync-error-msg">
            {errorMsg}
          </p>
        )}
      </div>
    </div>
  );
}
